




import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Modal,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { BASE_URL } from "../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import mapicon from "../../../assets/Dashboard/mapicon.svg";
import empty from "../../../assets/empty.png";
import { useTranslation } from "react-i18next";

const DepartmentPerformance = ({ dateRange }) => {
  const { company } = useSelector((state) => state.auth);
  const companyId = company?.id;
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { t } = useTranslation();

  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  // reportData now holds a SINGLE department row (same shape the UI already expects).
  // Internally it's derived from the report envelope's `data[]` array, since the API
  // returns an array of rows (one per department), not a single object.
  const [reportData, setReportData] = useState(null);
  const [chartDataFromApi, setChartDataFromApi] = useState(null); // reserved for future all-department chart
  const [openModal, setOpenModal] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);

  const [tempFilter, setTempFilter] = useState({
    startDate: "",
    endDate: "",
    department: "",
  });

  const getCurrentMonthRange = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const formatLocal = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    return { startDate: formatLocal(first), endDate: formatLocal(last) };
  };

  const formatDateTime = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatHours = (value) => {
    const num = parseFloat(value) || 0;
    const sign = num < 0 ? "-" : "";
    const abs = Math.abs(num);
    let hrs = Math.floor(abs);
    let mins = Math.round((abs - hrs) * 60);
    if (mins === 60) {
      hrs += 1;
      mins = 0;
    }
    return mins > 0 ? `${sign}${hrs}h ${mins}m` : `${sign}${hrs}h`;
  };

  // Fetch departments with token
  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${BASE_URL}/departments?no_pagination=true`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
      });

      if (!res.ok) {
        console.error("Department fetch failed:", res.status, res.statusText);
        return [];
      }

      const data = await res.json();

      if (!data.error && Array.isArray(data.data?.departments)) {
        const list = data.data.departments;
        setDepartments(list);
        return list;
      } else {
        console.error("Invalid department response:", data.message);
        return [];
      }
    } catch (e) {
      console.error("Error fetching departments:", e);
      return [];
    }
  };

  // Fetch report
  // departmentName: optional. Per API spec the `department` filter is the
  // department's NAME (string), not its id.
  const fetchReport = async (start, end, departmentName) => {
    try {
      const res = await fetch(`${BASE_URL}/public/reports/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
        body: JSON.stringify({
          type: "department_performance",
          startDate: start,
          endDate: end,
          companyId,
          showChartData: true,
          ...(departmentName ? { department: departmentName } : {}),
        }),
      });
      const result = await res.json();

      if (!result.error) {
        // result.data is the report envelope:
        // { data: [ {department_name, tasks_assigned, ...}, ... ], chart_data, summary_stats, summary }
        const rows = Array.isArray(result.data?.data) ? result.data.data : [];

        setChartDataFromApi(result.data?.chart_data || null);

        if (departmentName) {
          // A specific department was requested: the API returns exactly one row for it.
          setReportData(rows[0] || {});
        } else if (rows.length > 0) {
          // No department filter: show an aggregate across all departments using the
          // API's own summary_stats/summary rather than picking one row arbitrarily.
          const stats = result.data?.summary_stats || {};
          setReportData({
            department_name: t("reportsScreen.allDepartments"),
            tasks_assigned: stats.total_tasks_assigned,
            tasks_completed: stats.total_tasks_completed,
            productive_tasks: rows.reduce(
              (sum, r) => sum + (parseFloat(r.productive_tasks) || 0),
              0
            ),
            hours_worked: stats.total_hours_worked,
            compliance_percentage: stats.avg_compliance,
            productivity_percentage: stats.avg_productivity,
            efficiency_percentage: stats.avg_efficiency,
          });
        } else {
          setReportData({});
        }
      } else {
        setReportData({});
      }
    } catch (e) {
      console.error("Error fetching report:", e);
      setReportData({});
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!companyId) return;
      // Department is optional: fetch the department list for the filter dropdown,
      // but don't auto-select one — the first load should show all departments.
      await fetchDepartments();
      fetchReport(
        dateRange.startDate || getCurrentMonthRange().startDate,
        dateRange.endDate || getCurrentMonthRange().endDate
      );
    };
    init();
  }, [companyId]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const deptObj = departments.find((d) => d.id === selectedDept);
      fetchReport(dateRange.startDate, dateRange.endDate, deptObj?.name);
    }
  }, [dateRange]);

  const [tempDept, setTempDept] = useState("");

  const handleApply = () => {
    setOpenModal(false);
    setSelectedDept(tempFilter.department);
    setIsFiltered(!!tempFilter.department);
    const deptObj = departments.find((d) => d.id === tempFilter.department);
    fetchReport(dateRange.startDate, dateRange.endDate, deptObj?.name);
  };

  const handleClearFilter = () => {
    setSelectedDept(null);
    setIsFiltered(false);
    fetchReport(dateRange.startDate, dateRange.endDate);
  };

  const handleIcon = () => setOpenSummaryModal(true);
  const handleCloseSummary = () => setOpenSummaryModal(false);

  const hasValidData = (data) =>
    data && data.some((item) => parseFloat(item.value) > 0);

  if (!reportData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "70vh",
        }}
      >
        <CircularProgress size={48} thickness={4} sx={{ color: "#1976d2", mb: 2 }} />
      </Box>
    );
  }

  const taskBarData = [
    { name: t("reportsScreen.tasksAssigned"), value: parseFloat(reportData.tasks_assigned) || 0 },
    { name: t("reportsScreen.tasksCompleted"), value: parseFloat(reportData.tasks_completed) || 0 },
    { name: t("reportsScreen.productiveTasks"), value: parseFloat(reportData.productive_tasks) || 0 },
  ];

  const performancePieData = [
    { name: t("reportsScreen.compliance"), value: parseFloat(reportData.compliance_percentage) || 0 },
    { name: t("reportsScreen.productivity"), value: parseFloat(reportData.productivity_percentage) || 0 },
    { name: t("reportsScreen.efficiency"), value: parseFloat(reportData.efficiency_percentage) || 0 },
  ];

  const COLORS = ["#42A5F5", "#66BB6A", "#FFA726"];

  // Slices that fall on the left side get pushed toward the top instead of
  // extending leftward, so the label text stays inside the card. Bent labels
  // are stacked by slice index so two near-opposite slices don't collide.
  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name, fill, index }) => {
    const RADIAN = Math.PI / 180;
    const cos = Math.cos(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const arcX = cx + outerRadius * cos;
    const arcY = cy + outerRadius * sin;

    let bendX, bendY, labelX, labelY, anchor;
    if (cos < -0.3) {
      bendX = cx + (outerRadius + 10) * cos * 0.4;
      bendY = cy - outerRadius - 10 - index * 18;
      labelX = bendX;
      labelY = bendY - 8;
      anchor = "middle";
    } else {
      const radius = outerRadius + 20;
      bendX = cx + radius * cos;
      bendY = cy + radius * sin;
      labelX = bendX + (cos >= 0 ? 6 : -6);
      labelY = bendY;
      anchor = cos >= 0 ? "start" : "end";
    }

    return (
      <g>
        <path d={`M${arcX},${arcY}L${bendX},${bendY}`} stroke={fill} fill="none" />
        <text x={labelX} y={labelY} textAnchor={anchor} dominantBaseline="central" fontSize={12} fill={fill}>
          {`${name}: ${(percent * 100).toFixed(0)}%`}
        </text>
      </g>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <CustomText
          sx={{
            ...textStyles.h1,
            fontWeight: 600,
            fontSize: "18px",
            color: "#003149",
          }}
        >
          {t("reportsScreen.departmentTitle")}
        </CustomText>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              border: "1px solid #C4C4C4",
              borderRadius: 1,
              px: 2,
              py: 0.8,
              fontSize: 14,
              bgcolor: "#F9FAFB",
              color: "#003149",
              textAlign: "center",
              minWidth: "260px",
            }}
          >
            {reportData?.department_name
              ? `${reportData.department_name} | ${formatDateTime(
                  dateRange.startDate
                )} – ${formatDateTime(dateRange.endDate)}`
              : ""}
          </Box>
          <Box
            sx={{
              position: "relative",
              width: 36,
              height: 36,
              borderRadius: 1,
              border: "1px solid #C4C4C4",
              bgcolor: isFiltered ? "#1976d2" : "transparent",
              color: isFiltered ? "#fff" : "#003149",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={() => {
                setTempFilter({
                  ...dateRange,
                  department: selectedDept,
                });
                setOpenModal(true);
              }}
              sx={{
                width: "100%",
                height: "100%",
                color: "inherit",
                "&:hover": {
                  backgroundColor: isFiltered ? "#1565c0" : "#1976d220",
                },
              }}
            >
              <FilterAltIcon />
            </IconButton>
            {isFiltered && (
              <IconButton
                size="small"
                onClick={handleClearFilter}
                sx={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  backgroundColor: "#fff",
                  border: "1px solid #C4C4C4",
                  width: 18,
                  height: 18,
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <CloseIcon sx={{ fontSize: 13, color: "#333" }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        <Card sx={{ flex: "1 1 48%", p: 2, border: "2px solid #091E4224", borderRadius: 2, boxShadow: "none" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                color: "#44546F",
                fontWeight: 500,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("reportsScreen.department_task_compliance_report")}
            </Typography>
            <IconButton onClick={handleIcon}>
              <img src={mapicon} alt="map" style={{ width: 33, height: 33 }} />
            </IconButton>
          </Box>
          {hasValidData(taskBarData) ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={taskBarData} barCategoryGap="30%" barGap={10}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                  {taskBarData.map((entry, index) => {
                    const barColors = ["#1E88E5", "#26A69A", "#FFB300"]; // blue, teal, amber
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={barColors[index % barColors.length]}
                        stroke="#fff"
                        strokeWidth={1}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
            </Box>
          )}
        </Card>

        <Card sx={{ flex: "1 1 48%", p: 2, border: "2px solid #091E4224", borderRadius: 2, boxShadow: "none", overflow: "visible" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "15px",
                color: "#44546F",
                fontWeight: 500,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("reportsScreen.department_productivity_efficiency_report")}
            </Typography>
            <IconButton onClick={handleIcon}>
              <img src={mapicon} alt="map" style={{ width: 33, height: 33 }} />
            </IconButton>
          </Box>
          {hasValidData(performancePieData) ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart margin={{ top: 30, right: 20, bottom: 20, left: 20 }}>
                <Pie
                  data={performancePieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={75}
                  outerRadius={110}
                  paddingAngle={4}
                  dataKey="value"
                  labelLine={false}
                  label={renderPieLabel}
                >
                  {performancePieData.map((entry, index) => {
                    const COLORS = ["#29B6F6", "#81C784", "#FFD54F"]; // light blue, green, yellow
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    );
                  })}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" align="center" iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
            </Box>
          )}
        </Card>
      </Box>

      {/* Filter Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: 550,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#003149",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("reportsScreen.filterByDateDept")}
            </Typography>
            <IconButton onClick={() => setOpenModal(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <label className="field_label" style={{ marginTop: "16px" }}>
            {t("reportsScreen.department")}
          </label>
          <Select
            value={tempFilter.department}
            onChange={(e) => setTempFilter((p) => ({ ...p, department: e.target.value }))}
            fullWidth
            sx={{ mt: 1, height: "42px", fontSize: "15px", fontFamily: "Poppins, sans-serif" }}
          >
            <MenuItem value="" sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
              {t("reportsScreen.allDepartments")}
            </MenuItem>
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id} sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
                {d.name}
              </MenuItem>
            ))}
          </Select>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CustomButton label={t("reportsScreen.apply")} size="medium" onClick={handleApply} />
          </Box>
        </Box>
      </Modal>

      {/* Summary Modal */}
      <Modal open={openSummaryModal} onClose={handleCloseSummary}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 3,
            borderRadius: 3,
            width: 500,
            maxHeight: "80vh",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#003149",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("reportsScreen.summaryData")}
            </Typography>
            <IconButton onClick={handleCloseSummary}>
              <CloseIcon />
            </IconButton>
          </Box>

          {reportData ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                  <th style={{ padding: "10px" }}>{t("reportsScreen.metric")}</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>{t("reportsScreen.metric")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(reportData).map(([key, value]) => (
                  <tr key={key}>
                    <td
                      style={{
                        padding: "8px 10px",
                        borderBottom: "1px solid #eee",
                        textTransform: "capitalize",
                      }}
                    >
                      {key.replace(/_/g, " ")}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        borderBottom: "1px solid #eee",
                        textAlign: "right",
                      }}
                    >
                      {key === "start_date" || key === "end_date"
                        ? formatDateTime(value)
                        : /hours/i.test(key)
                        ? formatHours(value)
                        : String(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Typography>{t("reportsScreen.noData")}</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default DepartmentPerformance;


